import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faSearch,
  faTable,
  faInfoCircle,
  faMoneyCheckAlt, // More specific for settlements/payments
  faArchive,
  faHome, // For property/house details
  faCalendarDay, // For individual dates
} from "@fortawesome/free-solid-svg-icons";

import { StandardURL } from "../App";

const ArchivedDataSettlements = () => {
  const [year, setYear] = useState("");
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null); // New state for specific error message
  const [showResults, setShowResults] = useState(false); // To control visibility of results table

  const token = localStorage.getItem("token");
  const propertyId = localStorage.getItem("selectedPropertyId");

  const fetchSettlements = async () => {
    setSettlements([]); // Clear previous settlements
    setFetchError(null); // Clear previous errors
    setShowResults(false); // Hide results until new data is fetched

    if (!year.match(/^\d{4}$/)) {
      toast.error("Please enter a valid 4-digit year.");
      return;
    }

    if (!token || !propertyId) {
      setFetchError("Authentication token or property ID missing. Please log in and select a property.");
      toast.error("Authentication token or property ID missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/admin/settlementcheck/landlordbyyear/${propertyId}`,
        { year },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // Ensure response.data.response is treated as an array
      setSettlements(Array.isArray(response.data.response) ? response.data.response : []);
      setShowResults(true); // Show results section
      if (Array.isArray(response.data.response) && response.data.response.length === 0) {
        toast.info(`No archived settlement records found for the year ${year}.`);
      } else {
        toast.success("Archived settlement records fetched successfully!");
      }
    } catch (error) {
      console.error("Error fetching settlements:", error);
      if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
        setFetchError(`No settlement records found for the year ${year}.`);
        toast.info(`No settlement records found for the year ${year}.`);
      } else {
        setFetchError(error.response?.data?.response || "Failed to fetch settlements.");
        toast.error(error.response?.data?.response || "Failed to fetch settlements.");
      }
      setSettlements([]); // Clear settlements on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4 sm:mb-0">
                <FontAwesomeIcon icon={faArchive} className="mr-3 text-indigo-600" />
                Archived Landlord Settlements
              </h1>
            </div>

            {/* Year Input and Search Button */}
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8 p-6 bg-indigo-50 rounded-lg shadow-inner">
              <div className="flex-grow">
                <label htmlFor="yearInput" className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-indigo-500" />
                  Select Year
                </label>
                <input
                  id="yearInput"
                  type="number"
                  maxLength={4}
                  placeholder="YYYY"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                />
              </div>
              <button
                onClick={fetchSettlements}
                disabled={loading}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Oval height={20} width={20} color="#FFF" secondaryColor="#C7D2FE" strokeWidth={5} strokeWidthSecondary={5} />
                ) : (
                  <FontAwesomeIcon icon={faSearch} className="mr-2" />
                )}
                {loading ? "Searching..." : "View Settlements"}
              </button>
            </div>

            {loading && !showResults && (
              <div className="flex items-center justify-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#6366F1" // Indigo color
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel='oval-loading'
                  secondaryColor="#C7D2FE" // Lighter indigo
                  strokeWidth={4}
                  strokeWidthSecondary={4}
                />
                <p className="ml-3 text-lg text-gray-700">Fetching archived settlement records...</p>
              </div>
            )}

            {fetchError && (
              <div className="text-center py-10 bg-red-50 rounded-lg border border-red-300 text-red-700">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-red-400 mb-4" />
                <p className="text-lg font-semibold mb-2">Error!</p>
                <p className="text-md">{fetchError}</p>
                {!showResults && ( // Only show try again if results are not already being shown (e.g., from an empty search)
                  <button
                    onClick={fetchSettlements}
                    className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                  >
                    Try Again
                  </button>
                )}
              </div>
            )}

            {showResults && !loading && (
              settlements.length > 0 ? (
                <div className="overflow-x-auto mt-8">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    <FontAwesomeIcon icon={faTable} className="mr-2 text-indigo-600" />
                    Settlement Records for {year} ({settlements.length})
                  </h2>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md">
                    <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                      <tr>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Settlement ID
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Landlord Reference
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Processor Reference
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Mode
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Amount sent
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Transaction cost
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Status
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                          Settlement Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {settlements.map((item, index) => (
                        <tr
                          key={item.settlement_id || index}
                          className={index % 2 === 0 ? "bg-gray-50 hover:bg-gray-100" : "bg-white hover:bg-gray-100"}
                        >
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {item.settlement_id || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {item.reference || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {item.paymentreference || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            <FontAwesomeIcon icon={faHome} className="mr-1 text-gray-500" />
                            {item.mode || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                            <FontAwesomeIcon icon={faMoneyCheckAlt} className="mr-1 text-green-600" />
                            KES {parseFloat(item.total_amount_paid || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                            <FontAwesomeIcon icon={faMoneyCheckAlt} className="mr-1 text-green-600" />
                            KES {parseFloat(item.transactioncost || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {item.status || "N/A"}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-gray-600 text-sm">
                            <FontAwesomeIcon icon={faCalendarDay} className="mr-1 text-gray-500" />
                            {new Date(item.created_at).toLocaleDateString()}                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">No archived settlement records found for the selected year.</p>
                  <p className="text-md text-gray-500 mt-2">Please select a different year or ensure data exists.</p>
                </div>
              )
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default ArchivedDataSettlements;
